import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Reveal, WhatsAppButton } from '@/components/atoms';
import { Section } from '@/components/organisms';
import { introVehicles } from '@/config/introVehicles';

import { SITUATIONS_TITLE_ID, serviceSituations, WHATSAPP_MESSAGE } from '../content';
import { ServiceInquiryButton, ServiceSituationRow } from '../molecules';

/**
 * The same services, reached from the visitor's problem instead of our
 * catalogue.
 *
 * This is the band that makes the page worth building. The catalogue above
 * answers "what does Kabex sell", which is our question — nobody arrives with
 * it. They arrive with "my car is stuck at Mombasa", and a grid of nouns
 * asks them to translate that into our vocabulary before they can act. Here
 * the sentence they arrived with is the heading, and the service is the
 * destination.
 *
 * Situations rather than one row per service, because these are the questions
 * people actually turn up with.
 *
 * Two columns with the heading sticky on the left, matching the FAQ band below
 * it and `ImportFaq` elsewhere on the site: it keeps the way out in view while
 * the reader works down the list.
 */
export function ServiceSituationsBand() {
  return (
    <Section
      tone="muted"
      labelledBy={SITUATIONS_TITLE_ID}
      backdrop={{ picture: introVehicles.noah.image, placement: 'left' }}
    >
      <Grid container spacing={{ xs: 8, md: 12 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Reveal direction="left">
            {/* Sticky, and nothing up this tree may set `alignItems`: a
                stretched or centred flex child is as tall as its row, which
                leaves `position: sticky` no distance to travel. */}
            <Stack spacing={4} sx={{ position: { md: 'sticky' }, top: { md: 120 } }}>
              <Typography variant="overline" sx={{ color: 'primary.main' }}>
                Start here
              </Typography>
              <Typography id={SITUATIONS_TITLE_ID} variant="h2" component="h2">
                Where are you right now?
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Most people arrive with a situation rather than a service in mind. Find yours, and
                it will take you to the part that answers it.
              </Typography>
              <Stack spacing={3} sx={{ width: '100%', maxWidth: { xs: '100%', sm: 360 } }}>
                <ServiceInquiryButton />
                <WhatsAppButton message={WHATSAPP_MESSAGE} sx={{ alignSelf: 'flex-start' }}>
                  Or just ask us
                </WhatsAppButton>
              </Stack>
            </Stack>
          </Reveal>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Stack component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {serviceSituations.map((item) => (
              <ServiceSituationRow key={item.situation} item={item} />
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Section>
  );
}
